import { HttpClient, HttpHeaders, HttpParams, HttpEventType, HttpEvent } from "@angular/common/http"
import { EncryptionHandler } from "../../app/drive/security/encryption-handler"
import { SecurityHandler } from "../../app/drive/security/security-handler"
import queue from 'async/queue'
import { Observable, of, Subscriber } from "rxjs"
import { DownloadEvent, DownloadStartedEvent, DownloadProgressEvent, DownloadEventTypes, transformAngularDownloadEvent, DownloadCompletedEvent } from "./download-progress-event"
import { FileEntry } from "../uploads/file-entry"
import { Injectable } from "@angular/core"

@Injectable()
export class DownloadApiService {

    private url: string
    private fileEntry: FileEntry

    private chunkSize: number = 75 * 1024 * 1024
    private metadata: any
    private fileKey: string
    private chunkList: Array<any>
    private retries: number = 0
    private maxRetries: number = 6

    private totalLoaded: number = 0
    private timeStarted: number = 0

    private totalCachedCf: number = 0
    private totalCachedEdge: number = 0

    private fileStream: any
    private fileWriter: any
    private downloadStatus: Subscriber<unknown> = null

    constructor(
        private http: HttpClient
    ) {
    }

    public start(url: string, fileEntry: FileEntry): Observable<DownloadEvent> {
        this.url = url
        this.fileEntry = fileEntry

        return new Observable(observer => {
            this.downloadStatus = observer

            this.download().then((completedEvent: DownloadEvent) => {
                this.downloadStatus.next(completedEvent)
                this.downloadStatus.complete()
            })
        })
    }

    public async download(): Promise<DownloadEvent> {
        try {
            // set file decryption key
            const keyDec = this.fileEntry.keyDec || await new SecurityHandler().decryptKeyString(this.fileEntry.key)
            this.setFileKey(keyDec)

            this.timeStarted = Date.now();

            this.metadata = await this.getFileMetadata()
            const edgeServer = await this.getEdgeServer()
            this.metadata.url = this.metadata.url.replace('cdn.firefile.cc', edgeServer)
            this.chunkList = this.buildChunkList()

            this.metadata.filename = await this.decryptFilename(this.metadata.filename)

            this.fileStream = this.createFileStream()
            this.fileWriter = this.createFileWriter(this.fileStream)

            // Get first chunk and shift list by 1
            // Write first blob to ensure file is created
            const firstChunkRange = this.chunkList.slice(0, 1)[0]
            await this.downloadFirstChunk(firstChunkRange)

            const writeQueue = queue((item: any, callback: any) => {
                this.decryptChunk(item.blob).then((decryptedBlob) => {
                    this.writeChunk(this.fileWriter, decryptedBlob).then(() => {
                        callback()
                    })
                })
            }, 1)
            const downloadQueue = queue((chunk: any, callback: any) => {
                this.downloadChunk(chunk).then((blob: ArrayBuffer) => {
                    writeQueue.push({
                        blob,
                        chunk
                    }, () => {
                        callback()
                    })
                })
            }, 1)

            downloadQueue.push(this.chunkList.slice(1, this.chunkList.length))
            await downloadQueue.drain()
            await this.fileWriter.close()
            this.fileWriter.releaseLock()

            const completedEvent: DownloadEvent = {
                type: HttpEventType.Response,
                name: DownloadEventTypes.COMPLETED,
                fileEntry: this.fileEntry,
                totalCachedCf: this.totalCachedCf,
                totalCachedEdge: this.totalCachedEdge
            }
            return completedEvent
        } catch (err) {
            console.error(err)
        }
    }

    public setFileKey(key: string): void {
        this.fileKey = key
    }

    public getFileStream(): any {
        return this.fileStream
    }

    public getFileWriter(): any {
        return this.fileWriter
    }

    private async downloadFirstChunk(range: any): Promise<void> {
        let firstBlob = await this.downloadChunk(range)
        let decryptedBlob = await this.decryptChunk(firstBlob)
        // free up RAM
        firstBlob = null
        await this.writeChunk(this.fileWriter, decryptedBlob)
        // free up RAM
        decryptedBlob = null
    }

    private downloadChunk(range: any): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest()
            const headers = this.buildHttpHeader(range.start, range.end)
            xhr.open('GET', this.metadata.url, true)
            xhr.responseType = 'arraybuffer'
            Object.keys(headers).forEach(key => xhr.setRequestHeader(key, headers[key]))
            xhr.onload = (evt: ProgressEvent) => {
                if (xhr.status == 206) {
                    this.retries = 0
                    this.totalLoaded += evt.loaded
                    /**if (xhr.getResponseHeader('cf-cache-status')) {
                        const cached: boolean = xhr.getResponseHeader('cf-cache-status').toLowerCase() == 'hit' ? true : false
                        if (cached) {
                            this.totalCachedCf += evt.loaded
                        }
                    }*/
                    if (xhr.getAllResponseHeaders().indexOf('X-Edge-Cache') >= 0) {
                        if (xhr.getResponseHeader('X-Edge-Cache')) {
                            const cached: boolean = xhr.getResponseHeader('X-Edge-Cache').toLowerCase() == 'hit' ? true : false
                            if (cached) {
                                this.totalCachedEdge += evt.loaded
                            }
                        }
                    }
                    const chunkDownloadedEvent: DownloadEvent = {
                        type: 'DownloadChunkEvent',
                        name: DownloadEventTypes.CHUNK,
                        loaded: evt.loaded
                    }
                    this.downloadStatus.next(chunkDownloadedEvent)
                    resolve(xhr.response)
                } else xhr.onerror(evt)
            }
            xhr.onprogress = (ev: ProgressEvent) => {
                const httpEvent: HttpEvent<FileEntry> = {
                    type: HttpEventType.DownloadProgress,
                    loaded: this.totalLoaded + ev.loaded,
                    total: Number(this.metadata.size) || ev.total
                }
                const downloadEvent: DownloadEvent = transformAngularDownloadEvent(httpEvent, this.timeStarted)
                this.downloadStatus.next(downloadEvent)
            }
            xhr.onerror = (err: any) => {
                this.retries++
                console.error(err)
                console.log('retrying...', this.retries)
                if (this.retries < this.maxRetries) {
                    setTimeout(() => {
                        this.downloadChunk(range).then((data) => resolve(data)).catch((err) => reject(err))
                    }, 1000)
                } else {
                    this.retries = 0
                    reject(err)
                }
            }
            xhr.send()
        })
    }

    private async decryptChunk(chunk: ArrayBuffer): Promise<ArrayBuffer> {
        const iv: Uint8Array = new Uint8Array(chunk.slice(0, 16))
        let data: Blob = await new Response(chunk.slice(16)).blob()
        // free up RAM
        chunk = null
        const encryptionHandler = EncryptionHandler.getDecryptor(iv)

        encryptionHandler.setIV(iv)
        await encryptionHandler.setKey(this.fileKey)
        const decryptedChunk = await encryptionHandler.decryptBlob(data)
        // free up RAM
        data = null

        return decryptedChunk
    }

    private getFileMetadata() {
        return new Promise((resolve) => {
            this.http.get(this.url, this.buildHttpOptionsJson(0, 1)).subscribe((event: HttpEvent<any>) => {
                if (event.type == HttpEventType.Response) {
                    const metadata = {
                        filename: event.headers.get('Content-disposition').split('filename=')[1].replace(/^"(.*)"$/, '$1'),
                        size: event.headers.get('Content-Range').split('/')[1],
                        mime: event.headers.get('Content-Type'),
                        url: event.body.url,
                    }
                    resolve(metadata)
                }
            })
        })
    }

    private async decryptFilename(filename: string) {
        const securityHandler: SecurityHandler = new SecurityHandler()
        const decFilename: string = await securityHandler.decryptString(filename, this.fileKey)

        return decFilename
    }

    private createFileStream() {
        const window = this.getWindow()
        const streamSaver = window.streamSaver
        streamSaver.mitm = 'https://firefile.cc/mitm.html'
        return streamSaver.createWriteStream(this.metadata.filename, {
            size: this.metadata.size
        })
    }

    private createFileWriter(fileStream: any) {
        return fileStream.getWriter()
    }

    private writeChunk(fileWriter: any, chunk: ArrayBuffer) {
        return new Promise((resolve, reject) => {
            const readableStream = new Response(chunk).body
            const reader = readableStream.getReader()
            const pump = () => reader.read().then(res => {
                res.done ? resolve() : fileWriter.write(res.value).then(pump)
            }).catch((err: any) => reject(err))
            pump()
        })
    }

    private buildChunkList(): Array<any> {
        const size: number = Number(this.metadata.size)
        this.chunkSize += 32
        const chunkCount = Math.ceil(size / this.chunkSize)

        let chunks = []
        for (let i = 0; i < chunkCount; i++) {
            //let start: number = (i * this.chunkSize) + (i * 1)
            let start: number = i * this.chunkSize
            let end: number = start + this.chunkSize - 1

            if (end > size) {
                const diff = end - size
                end -= diff
            }

            const range = {
                start,
                end
            }
            chunks.push(range)
        }

        return chunks
    }

    private async getEdgeServer() {
        const response = await this.http.get('https://dns.firefile.cc/edge', {
            responseType: 'text'
        }).toPromise()
        return response
    }

    private getWindow(): any {
        let _window: any = window
        return _window
    }

    private buildHttpHeader(offset: number, length: number) {
        return {
            'Range': 'bytes=' + offset + '-' + length
        }
    }

    private buildHttpOptionsJson(offset: number, length: number) {
        let options: {
            headers?: HttpHeaders | {
                [header: string]: string | string[];
            };
            observe: 'events';
            params?: HttpParams | {
                [param: string]: string | string[];
            };
            reportProgress?: boolean;
            responseType: 'json';
            withCredentials?: boolean;
        } = {
            headers: {
                'Range': 'bytes=' + offset + '-' + length
            },
            observe: 'events',
            responseType: 'json',
            reportProgress: true
        }

        return options
    }
}